/*
 * audio track controller
*/

import Event from '../events';
import EventHandler from '../event-handler';
import { logger } from '../utils/logger';

class AudioTrackController extends EventHandler {

	constructor(hls) {
		super(hls, Event.MANIFEST_LOADING,
			Event.MANIFEST_LOADED,
			/*
			Event.AUDIO_TRACK_LOADED);
			*/
			//Mactrix changes begin
			//add audio rendition and stream coordinator support
			Event.AUDIO_TRACK_LOADED,
			Event.LEVEL_SWITCHING,
			Event.STREAM_ALL_LEVELS_LOADED
		);
		//Mactrix changes end
		this.ticks = 0;
		this.ontick = this.tick.bind(this);
	//Mactrix additions start
		this._allLevelsLoaded = false;
		this._pendingInterval = undefined;
		this._timersActive = 0;
	//Mactrix additions end
	}

	destroy() {
		EventHandler.prototype.destroy.call(this);
	//Mactrix additions start
	//memory clean up
		this.ontick = null;
	//this.tracks = null;
	//this.hls = null;
	//Mactrix additions end
	}

	clearTimer() {
		if (this.timer) {
			clearInterval(this.timer);
			this.timer = null;
			this._timersActive--;
		}
	}

	incrementTimerCount() {
		this._timersActive++;
		if(this._timersActive > 1) {
			logger.warn(`Timer leak in audio track controller, number of active timers: ${this._timersActive}`);
		}
	}

  //Mactrix additions start
  //stop audio track reloads on stopLoad
	stopLoad() {
		this.clearTimer();
	}
  //Mactrix additions end

	tick() {
		this.ticks++;
		if (this.ticks === 1) {
			this.doTick();
			if (this.ticks > 1) {
				setTimeout(this.tick, 1);
			}
			this.ticks = 0;
		}
	}
	doTick() {
		this.updateTrack(this.trackId);
	}
	onManifestLoading() {
	// reset audio tracks on manifest loading
		this.tracks = [];
		this.trackId = -1;
	}

	onManifestLoaded(data) {
		let tracks = data.audioTracks || [];
		let defaultFound = false;
		this.tracks = tracks;
//Mactrix changes start
//Moving this to lower down
//    this.hls.trigger(Event.AUDIO_TRACKS_UPDATED, {audioTracks : tracks});
//Mactrix changes end
	// loop through available audio tracks and autoselect default if needed
		let id = 0;
		tracks.forEach(track => {
			if(track.default) {
				this.audioTrack = id;
				defaultFound = true;
				return;
			}
			id++;
		});
		if (defaultFound === false && tracks.length) {
			logger.log('no default audio track defined, use first audio track as default');
			this.audioTrack = 0;
		}
//Mactrix changes start
//Moving this from above; it doesn't make much sense to me to report an update and THEN change the tracks...
		this.hls.trigger(Event.AUDIO_TRACKS_UPDATED, { audioTracks : tracks });
//Mactrix changes end
	}

	onAudioTrackLoaded(data) {
		if (data.id < this.tracks.length) {
			logger.log(`audioTrack ${data.id} loaded`);
			this.tracks[data.id].details = data.details;
			// check if current playlist is a live playlist
			if (data.details.live && !this.timer) {
				// if live playlist we will have to reload it periodically
				// set reload period to playlist target duration
				if(this._allLevelsLoaded) { //Mactrix line added
					this.clearTimer();
					this.timer = setInterval(this.ontick, 1000 * data.details.targetduration);
					this.incrementTimerCount();
		//Mactrix additions start
					//don't start reloading manifests until STREAM_ALL_LEVELS_LOADED
				}
				else {
					logger.log('delaying arming timer until STREAM_ALL_LEVELS_LOADED');
					this._pendingInterval = 1000 * data.details.targetduration;
				}
		//Mactrix additions end
			}
			if (!data.details.live && this.timer) {
				// playlist is not live and timer is armed : stopping it
				this.clearTimer();
			}
		}
	}

	//Mactrix additions start
	//don't start reloading manifests until STREAM_ALL_LEVELS_LOADED
	onStreamAllLevelsLoaded() {
		this._allLevelsLoaded = true;
		if(this._pendingInterval) {
			this.clearTimer();
			this.timer = setInterval(this.ontick, this._pendingInterval);
			this.incrementTimerCount();
		}
	}
	//Mactrix additions end

	//Mactrix additions start
	//on level switch, we want to run updateTrack, so we can change the rendition group we're loading from if needed
	onLevelSwitching() {
		if(this.trackId >= 0) {
			this.updateTrack(this.trackId);
		}
	}
	//Mactrix additions end

  /** get alternate audio tracks list from playlist **/
	get audioTracks() {
		return this.tracks;
	}

  /** get index of the selected audio track (index in audio track lists) **/
	get audioTrack() {
		return this.trackId;
	}

  /** select an audio track, based on its index in audio track lists**/
	set audioTrack(audioTrackId) {
		if (this.trackId !== audioTrackId || this.tracks[audioTrackId].details === undefined) {
			this.setAudioTrackInternal(audioTrackId);
		}
	}

	setAudioTrackInternal(newId) {
		// check if level idx is valid
		if (newId >= 0 && newId < this.tracks.length) {
			// stopping live reloading timer if any
			this.clearTimer();
			this.trackId = newId;
			logger.log(`switching to audioTrack ${newId}`);

			let audioTrack = this.tracks[newId];
			let hls = this.hls;
			let type = audioTrack.type;
			let url = audioTrack.renditions[this.getRenditionNumber(newId)].url;
			let eventObj = { id: newId, type : type, url : url };

			// keep AUDIO_TRACK_SWITCH for legacy reason
			hls.trigger(Event.AUDIO_TRACK_SWITCH, eventObj);
			hls.trigger(Event.AUDIO_TRACK_SWITCHING, eventObj);

			// check if we need to load playlist for this audio Track
			let details = audioTrack.details;
			if (url && (details === undefined || details.live === true)) {
				// track not retrieved yet, or live playlist we need to (re)load it
				logger.log(`(re)loading playlist for audioTrack ${newId}`);
				hls.trigger(Event.AUDIO_TRACK_LOADING, { url: url, id: newId });
			}
		}
	}

	updateTrack(newId) {
		// check if level idx is valid
		if (newId >= 0 && newId < this.tracks.length) {
			// stopping live reloading timer if any
			this.clearTimer();
			this.trackId = newId;
			logger.log(`updating audioTrack ${newId}`);

			/*
			let audioTrack = this.tracks[newId], url = audioTrack.url;
			*/

			let audioTrack = this.tracks[newId];
			let url = audioTrack.renditions[this.getRenditionNumber(newId)].url;

			// check if we need to load playlist for this audio Track
			let details = audioTrack.details;
			if (url && (details === undefined || details.live === true)) {
				// track not retrieved yet, or live playlist we need to (re)load it
				logger.log(`(re)loading playlist for audioTrack ${newId}`);
				this.hls.trigger(Event.AUDIO_TRACK_LOADING, { url: url, id: newId });
			}
		}
	}

	//Mactrix additions start
	//used to determine what audio rendition group we need to be using
	getRenditionNumber(trackId) {
		let audioTrack = this.tracks[trackId];
		let hls = this.hls;
		let rendition = 0;

		if(hls.levels && hls.nextLoadLevel >= 0) {
			let levelAudioGroup = hls.levels[hls.nextLoadLevel].audioGroup;

			if (levelAudioGroup) {
				for (let i = 0; i < audioTrack.renditions.length; i++) {
					if (audioTrack.renditions[i].groupId === levelAudioGroup) {
						rendition = i;
					}
				}
			}
		}

		return rendition;
	}
  //Mactrix additions end
}

export default AudioTrackController;



// WEBPACK FOOTER //
// ./src/hlsjs/controller/audio-track-controller.js